/**
 * Kartenaufbau eines Abstiegs.
 *
 * Räume werden aus handgebauten Bausteinen prozedural ARRANGIERT — nie rein
 * algorithmisch erzeugt (Dossier §3.3: reine Algorithmen-Layouts erzeugen
 * "leicht unterschiedliche Korridore" und langweilen).
 *
 * Nach dem ersten Spieltest offener gebaut (Ueberarbeitung B1): größere
 * Grundfläche, deutlich größere Kammern, Gänge fünf bis sieben Zellen breit,
 * und ein neuer Bausteintyp **Halle** — eine große Fläche, die durch Säulen
 * gegliedert wird statt durch Wände. Alle Maße stehen im Regelwerk.
 *
 * Regel aus docs/design/art-direction.md §5.2: Jeder Abstieg enthält mindestens
 * EIN Wahrzeichen. Ein Raum ohne Wahrzeichen ist ein Korridor.
 */

import type { Strom } from '@pg/rng';
import type { Regelwerk } from '@pg/content';
import type { Karte, Raum } from './typen.js';
import { vek } from './vektor.js';

export const FELS = 0;
export const BODEN = 1;
export const WAHRZEICHEN_BODEN = 2;

/** Wahrzeichen: groß, einmalig, nicht wiederholt. */
const WAHRZEICHEN: ReadonlyArray<{ id: string; b: number; h: number }> = [
  { id: 'eingestuerzte-esse', b: 23, h: 21 },
  { id: 'bruecke-ueber-glut', b: 29, h: 17 },
  { id: 'lagerhaus-regalgassen', b: 25, h: 23 },
  { id: 'gerissener-kessel', b: 21, h: 21 },
  { id: 'verschuettete-treppe', b: 19, h: 25 },
];

export interface KartenPlan {
  breite: number;
  hoehe: number;
  kammern: number;
  hallen: number;
  gangbreite: number;
}

export function planFuerTiefe(tiefe: number, r: Regelwerk): KartenPlan {
  const k = r.karte;
  const spanne = Math.min(k.grundflaeche.hoechstens, Math.round(k.grundflaeche.basis + tiefe * k.grundflaeche.jeTiefe));
  const kammern = Math.min(
    k.kammern.hoechstens,
    k.kammern.basis + Math.floor((tiefe / 12) * k.kammern.jeZwoelfTiefen),
  );
  return {
    breite: spanne,
    hoehe: spanne,
    kammern,
    hallen: tiefe >= k.hallen.abTiefe ? k.hallen.anzahl : 0,
    gangbreite: k.gangbreite.min,
  };
}

function setze(k: Karte, x: number, y: number, wert: number): void {
  if (x < 0 || y < 0 || x >= k.breite || y >= k.hoehe) return;
  k.zellen[y * k.breite + x] = wert;
}

export function zelle(k: Karte, x: number, y: number): number {
  if (x < 0 || y < 0 || x >= k.breite || y >= k.hoehe) return FELS;
  return k.zellen[y * k.breite + x] as number;
}

/** Ist der Punkt begehbar? Weltkoordinaten, nicht Zellen. */
export function begehbar(k: Karte, x: number, y: number): boolean {
  return zelle(k, Math.floor(x), Math.floor(y)) !== FELS;
}

function malRaum(k: Karte, r: Raum, wert: number): void {
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.b; x++) setze(k, x, y, wert);
  }
}

/**
 * Säulen in einer Halle: kleine Felsinseln, die eine große Fläche gliedern,
 * ohne sie in Gänge zu zerschneiden. Das ist der Unterschied zwischen "offen"
 * und "leer".
 */
function setzeSaeulen(k: Karte, raum: Raum, strom: Strom): void {
  const abstandX = 7;
  const abstandY = 6;
  for (let y = raum.y + 4; y < raum.y + raum.h - 4; y += abstandY) {
    for (let x = raum.x + 4; x < raum.x + raum.b - 4; x += abstandX) {
      if (!strom.chance(0.72)) continue;
      const gr = strom.ganz(1, 2);
      const vx = x + strom.ganz(-1, 1);
      const vy = y + strom.ganz(-1, 1);
      for (let dy = 0; dy < gr; dy++) {
        for (let dx = 0; dx < gr; dx++) setze(k, vx + dx, vy + dy, FELS);
      }
    }
  }
}

/** Ein Gang mit Knick — nie diagonal, breit genug, dass er kein Schlauch ist. */
function malGang(k: Karte, ax: number, ay: number, bx: number, by: number, breite: number): void {
  const h = Math.floor(breite / 2);
  const x1 = Math.min(ax, bx);
  const x2 = Math.max(ax, bx);
  for (let x = x1; x <= x2; x++) {
    for (let d = -h; d <= h; d++) setze(k, x, ay + d, BODEN);
  }
  const y1 = Math.min(ay, by);
  const y2 = Math.max(ay, by);
  for (let y = y1; y <= y2; y++) {
    for (let d = -h; d <= h; d++) setze(k, bx + d, y, BODEN);
  }
}

function ueberschneidet(a: Raum, b: Raum, abstand: number): boolean {
  return (
    a.x - abstand < b.x + b.b &&
    a.x + a.b + abstand > b.x &&
    a.y - abstand < b.y + b.h &&
    a.y + a.h + abstand > b.y
  );
}

function mitte(r: Raum): { x: number; y: number } {
  return { x: Math.floor(r.x + r.b / 2), y: Math.floor(r.y + r.h / 2) };
}

export function baueKarte(strom: Strom, plan: KartenPlan, r: Regelwerk): Karte {
  const karte: Karte = {
    breite: plan.breite,
    hoehe: plan.hoehe,
    zellen: new Uint8Array(plan.breite * plan.hoehe),
    raeume: [],
    start: vek(),
    ziel: vek(),
  };

  const raeume: Raum[] = [];

  const platziere = (b: number, h: number, art: Raum['art'], versuche = 80): Raum | null => {
    for (let i = 0; i < versuche; i++) {
      const x = strom.ganz(2, Math.max(2, plan.breite - b - 3));
      const y = strom.ganz(2, Math.max(2, plan.hoehe - h - 3));
      const kandidat: Raum = { x, y, b, h, art };
      // Abstand 4: genug fuer eine Wand, wenig genug fuer eine offene Anlage.
      if (raeume.some((rr) => ueberschneidet(kandidat, rr, 4))) continue;
      raeume.push(kandidat);
      return kandidat;
    }
    return null;
  };

  // 1. Startkammer — grosszuegig, damit der erste Eindruck nicht eng ist.
  const start = platziere(15, 13, 'start') ?? { x: 2, y: 2, b: 15, h: 13, art: 'start' as const };
  if (!raeume.includes(start)) raeume.push(start);

  // 2. Hallen — die offenen Flaechen, die den Schlauchcharakter brechen.
  const halleGewichte = r.karte.halleFormen.map((f) => f.gewicht);
  const hallen: Raum[] = [];
  for (let i = 0; i < plan.hallen; i++) {
    const form = strom.gewichtet(r.karte.halleFormen, halleGewichte);
    const raum = platziere(form.b, form.h, 'kammer');
    if (raum) hallen.push(raum);
  }

  // 3. Ein Wahrzeichen — Pflicht, nicht optional.
  const wz = strom.waehle(WAHRZEICHEN);
  const wzRaum = platziere(wz.b, wz.h, 'wahrzeichen');
  if (wzRaum) wzRaum.wahrzeichen = wz.id;

  // 4. Kammern dazwischen
  const kammerGewichte = r.karte.kammerFormen.map((f) => f.gewicht);
  for (let i = 0; i < plan.kammern; i++) {
    const form = strom.gewichtet(r.karte.kammerFormen, kammerGewichte);
    platziere(form.b, form.h, 'kammer');
  }

  // 5. Ausklangkammer — muss WEIT weg vom Start liegen.
  //
  // Der erste Entwurf hatte einen blinden Rueckfall auf eine feste Ecke, wenn
  // kein Platz gefunden wurde. Gemessen stand der Ausklang-Gegner dadurch
  // gelegentlich zwei Sekunden vom Start entfernt — ein Charakter der Stufe 1
  // lief direkt in einen Gegner mit dem Zwanzigfachen seiner Lebenskraft.
  // Jetzt wird notfalls der ENTFERNTESTE freie Platz genommen, nie ein fester.
  const s0 = mitte(start);
  const mindestabstand = plan.breite * 0.42;
  let ausklang: Raum | null = null;
  let bestesAusweich: Raum | null = null;
  let besteEntfernung = -1;

  for (let i = 0; i < 200; i++) {
    const b = 23;
    const h = 21;
    const x = strom.ganz(2, Math.max(2, plan.breite - b - 3));
    const y = strom.ganz(2, Math.max(2, plan.hoehe - h - 3));
    const kandidat: Raum = { x, y, b, h, art: 'ausklang' };
    if (raeume.some((rr) => ueberschneidet(kandidat, rr, 4))) continue;

    const m = mitte(kandidat);
    const entfernung = Math.hypot(m.x - s0.x, m.y - s0.y);
    if (entfernung > besteEntfernung) {
      besteEntfernung = entfernung;
      bestesAusweich = kandidat;
    }
    if (entfernung >= mindestabstand) {
      ausklang = kandidat;
      break;
    }
  }

  if (!ausklang) ausklang = bestesAusweich;
  if (!ausklang) {
    // Wirklich kein Platz: den vom Start entferntesten vorhandenen Raum umwidmen.
    let weitester = raeume[0] as Raum;
    let weit = -1;
    for (const rr of raeume) {
      if (rr === start) continue;
      const m = mitte(rr);
      const e = Math.hypot(m.x - s0.x, m.y - s0.y);
      if (e > weit) {
        weit = e;
        weitester = rr;
      }
    }
    weitester.art = 'ausklang';
    ausklang = weitester;
  } else {
    raeume.push(ausklang);
  }

  // 6. Boden malen
  for (const raum of raeume) {
    malRaum(karte, raum, raum.art === 'wahrzeichen' ? WAHRZEICHEN_BODEN : BODEN);
  }

  // 7. Verbinden. Sortierung deterministisch (Abstand, dann x, dann y) —
  //    nie Map-Reihenfolge, sonst ist der Lauf nicht reproduzierbar.
  const s = s0;
  const sortiert = raeume
    .filter((rr) => rr !== start)
    .map((rr) => ({ raum: rr, m: mitte(rr) }))
    .sort((a, b) => {
      const da = (a.m.x - s.x) ** 2 + (a.m.y - s.y) ** 2;
      const db = (b.m.x - s.x) ** 2 + (b.m.y - s.y) ** 2;
      return da - db || a.m.x - b.m.x || a.m.y - b.m.y;
    });

  let vorher = s;
  for (const { m } of sortiert) {
    const breite = strom.ganz(r.karte.gangbreite.min, r.karte.gangbreite.max);
    malGang(karte, vorher.x, vorher.y, m.x, m.y, breite);
    vorher = m;
  }

  // Querverbindungen: ohne sie bleibt die Anlage trotz breiter Gaenge eine Kette.
  if (sortiert.length >= 3) {
    const a = mitte(sortiert[0]!.raum);
    const b = mitte(sortiert[sortiert.length - 1]!.raum);
    malGang(karte, a.x, a.y, b.x, b.y, r.karte.gangbreite.max);
    const c = mitte(sortiert[Math.floor(sortiert.length / 2)]!.raum);
    malGang(karte, s.x, s.y, c.x, c.y, r.karte.gangbreite.min);
  }

  // 8. Saeulen in die Hallen — NACH dem Gangbau, sonst werden sie weggemalt.
  for (const halle of hallen) setzeSaeulen(karte, halle, strom);

  karte.raeume = raeume;
  karte.start = vek(s.x + 0.5, s.y + 0.5);
  const z = mitte(ausklang);
  karte.ziel = vek(z.x + 0.5, z.y + 0.5);
  return karte;
}

/** Nächster begehbarer Punkt — für Spawns, die im Fels gelandet sind. */
export function naechsterBoden(k: Karte, x: number, y: number): { x: number; y: number } {
  if (begehbar(k, x, y)) return { x, y };
  for (let r = 1; r < 14; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        if (begehbar(k, x + dx, y + dy)) return { x: x + dx, y: y + dy };
      }
    }
  }
  return { x: k.start.x, y: k.start.y };
}

/** Anteil begehbarer Zellen — Kennzahl fuer "offen statt schlauchartig". */
export function offenheit(k: Karte): number {
  let boden = 0;
  for (let i = 0; i < k.zellen.length; i++) if (k.zellen[i] !== FELS) boden++;
  return boden / k.zellen.length;
}
